// src/components/public/PasswordResetModal.js
// "Forgot password?" dialog. Sends a Firebase password-reset email.
// Used by the homepage sign-in card and the /login page.
import React, { useState } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase';

const PasswordResetModal = ({ initialEmail = '', onClose }) => {
  const [resetEmail, setResetEmail] = useState(initialEmail);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState({ text: '', type: '' });

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetMessage({ text: '', type: '' });

    if (!resetEmail.trim()) {
      setResetMessage({ text: 'Enter your email.', type: 'error' });
      setResetLoading(false);
      return;
    }

    try {
      await sendPasswordResetEmail(auth, resetEmail.trim());
      setResetMessage({
        text: 'Check your email for a reset link. It may be in spam.',
        type: 'success'
      });
      setTimeout(onClose, 3000);
    } catch (err) {
      let errorMessage = "Couldn't send the reset email. ";
      switch (err.code) {
        case 'auth/user-not-found':
          errorMessage += 'No account uses this email.';
          break;
        case 'auth/invalid-email':
          errorMessage += "That email isn't valid.";
          break;
        case 'auth/too-many-requests':
          errorMessage += 'Too many attempts. Try again later.';
          break;
        default:
          errorMessage += 'Try again.';
          console.error('Password Reset Error:', err);
      }
      setResetMessage({ text: errorMessage, type: 'error' });
    }
    setResetLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[70]">
      <div role="dialog" aria-modal="true" aria-labelledby="reset-title" className="pub-surface pub-line border rounded-2xl shadow-2xl shadow-black/30 p-8 sm:p-10 max-w-md w-full">
        <div className="flex justify-between items-start gap-4">
          <h2 id="reset-title" className="pub-text text-2xl sm:text-3xl font-extrabold tracking-tight">Reset password</h2>
          <button onClick={onClose} aria-label="Close" className="pub-faint p-1 hover:opacity-80 transition">
            <i className="fas fa-xmark text-xl"></i>
          </button>
        </div>

        <p className="pub-muted mt-2 mb-6">
          We'll email you a link to reset your password.
        </p>

        <form onSubmit={handlePasswordReset} className="space-y-5">
          <div>
            <label htmlFor="resetEmail" className="pub-text block font-semibold mb-2">
              Email
            </label>
            <div className="relative">
              <i className="fas fa-envelope pub-faint absolute left-4 top-1/2 -translate-y-1/2"></i>
              <input
                id="resetEmail"
                type="email"
                autoComplete="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="pub-input w-full py-3.5 rounded-lg transition pl-12 pr-4"
                placeholder="you@company.com"
                required
                autoFocus
              />
            </div>
          </div>

          {resetMessage.text && (
            <div role="status" className={`px-4 py-3 rounded-lg text-sm font-semibold flex items-center gap-3 ${
              resetMessage.type === 'success' ? 'pub-success' : 'pub-error'
            }`}>
              <i className={resetMessage.type === 'success' ? 'fas fa-circle-check' : 'fas fa-circle-exclamation'}></i>
              <span>{resetMessage.text}</span>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button
              type="button"
              onClick={onClose}
              className="pub-surface-2 pub-text pub-line border flex-1 py-3.5 rounded-lg font-semibold hover:opacity-80 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={resetLoading}
              className="flex-1 flex items-center justify-center gap-3 py-3.5 rounded-lg pub-btn font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {resetLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Sending…
                </>
              ) : 'Send reset link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordResetModal;
